import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import { MAX_DOWNLOAD_LIMIT } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { getCurrentTimestamp } from "@saleor/reports/utils/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useActiveBatchDetailsReportQuery } from "../../../batch/queries";
import { getYear } from "../../../batch/utils/handlers";
import { reportsDownload } from "../../../utils/utils";
import BatchHistoryListPage from "../../components/BatchHistoryListPage";
import { useBatchListHistoryQuery } from "../../queries";
import { batchHistoryUrl } from "../../urls";
import { filterReportData, getThreeMonths } from "../../utils/handlers";

function BatchHistoryList({ params }) {
  const intl = useIntl();
  const paginate = usePaginator();
  const notify = useNotifier();
  const navigate = useNavigator();

  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  const { isSelected, listElements, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const { updateListSettings, settings } = useListSettings(
    ListViews.PAYOUT_DETAILS_LIST
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: {
        generatedAt: {
          // Fixed values, 3 months until Today
          gte: getThreeMonths().from,
          lte: getThreeMonths().to
        }
      }
    }),
    [params]
  );

  const { data, refetch } = useBatchListHistoryQuery({
    displayLoader: true,
    skip: false,
    variables: previewVariables
  });

  // reused endpoint since it is expecting to download same data from activeBatchDetailsReport
  const { data: activeBatchDetailsReport } = useActiveBatchDetailsReportQuery({
    displayLoader: true,
    skip: listElements.length === 0,
    variables: {
      ids: listElements
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers(
    navigate,
    batchHistoryUrl,
    params
  );

  const arr = activeBatchDetailsReport?.activeBatchDetailsReport.edges.map(
    a => a.node
  );

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.batchHistory.pageInfo),
    paginationState,
    params
  );

  useEffect(() => {
    if (listElements.length > MAX_DOWNLOAD_LIMIT) {
      notify({
        status: "error",
        text: "You’ve reached the maximum number of batches to be downloaded"
      });
      // removes last added element
      listElements.pop();
    } else {
      refetch();
    }
  }, [settings.rowNumber, listElements]);

  const handleDownload = () => {
    // multiple file download

    setDownloadStatus("loading");

    const groupedArr = Object.keys(_.groupBy(arr, "id"));

    for (let i = 0; i <= groupedArr.length; i++) {
      const val = filterReportData(
        _.groupBy(arr, "id")[Object.keys(_.groupBy(arr, "id"))[i]],
        reportsDownload.batchHistoryListPayouts.fields
      ).map(item => {
        Object.entries(item).forEach(([key, value]) => {
          if (
            typeof value === "string" &&
            value?.includes(",") &&
            !value?.match(/^".*?"$/)
          ) {
            item[key] = `"${value}"`;
          }
        });

        return item;
      });

      const fileNameBatchId = Object.values(
        _.groupBy(arr, "id")[groupedArr[i]]
      )[0].id;
      const fileNameMallCode = Object.values(
        _.groupBy(arr, "id")[groupedArr[i]]
      )[0].mall_code;
      const fileNameYear = getYear(
        Object.values(_.groupBy(arr, "id")[groupedArr[i]])[0].created
      );

      useDownloadReport(
        val,
        reportsDownload?.batchHistoryListPayouts?.fields,
        `${fileNameMallCode}_${fileNameYear}_${atob(fileNameBatchId)}`,
        `${fileNameMallCode}_${fileNameYear}_${atob(
          fileNameBatchId
        )}-${getCurrentTimestamp()}`,
        "csv"
      );

      // checks if for loop iteration is done
      // ensures to call notify only once
      if (i + 1 >= Object.keys(_.groupBy(arr, "id")).length) {
        setDownloadStatus("success");
        notify({
          status: "success",
          text: "Successfully downloaded the report"
        });
        closeModal();
      }
    }
  };

  return (
    <div>
      <BatchHistoryListPage
        batchHistories={data?.batchHistory?.edges?.map(edge => edge.node)}
        isChecked={isSelected}
        numberOfColumns={7}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={(items, selected) =>
          toggleAll(items, selected, MAX_DOWNLOAD_LIMIT)
        }
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        settings={settings}
        listElements={listElements}
        download={() => openModal("download")}
      />

      <ActionDialog
        open={
          params.action === "download" &&
          Object.keys(_.groupBy(arr, "id")).length >= 1
        }
        onClose={closeModal}
        confirmButtonState={downloadStatus}
        onConfirm={handleDownload}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Batch",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download batch with {total} row{s}. Do you want to continue?"
            values={{
              s: Object.keys(_.groupBy(arr, "id")).length > 1 ? "s" : "",
              total: Object.keys(_.groupBy(arr, "id")).length
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </div>
  );
}

export default BatchHistoryList;
